import { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import {
  NativeVideoAdapter,
  VideoJsAdapter,
  type VideoAdapter,
} from './videoAdapter';

import type Player from 'video.js/dist/types/player';

import { useAnalytics } from './analytics';
import {
  MediaAnalyticsEvents,
  MediaEventsWithProperties,
  MediaPlayerTimedEvent,
  ProjectType,
} from './events';

interface WatchSession {
  startTime: number;
  endTime?: number;
  duration: number;
}

interface SkipRange {
  start: number;
  end: number;
}

interface VideoAnalyticsState {
  totalWatchTime: number;
  actualWatchTime: number;
  skippedRanges: SkipRange[]; // Keep track of ranges to handle overlaps
  currentSession: WatchSession | null;
  isPlaying: boolean;
  hasStarted: boolean;
  lastKnownPosition: number;
  seekStartTime: number;
}

const HEARTBEAT_INTERVAL = 15000;
// Math.round() is the standard choice for video analytics.
const round = Math.round;

export const useVideoAnalytics = (props?: {
  projectType?: ProjectType;
  projectId?: string;
  publicationId?: string;
}) => {
  const analyticsParams = useMemo(() => {
    const propsParams = {
      project_type: props?.projectType,
      project_id: props?.projectId,
      publication_id: props?.publicationId,
    };
    // Filter out undefined values to allow superProperties to take precedence
    return Object.fromEntries(
      Object.entries(propsParams).filter(([_, v]) => v !== undefined),
    );
  }, [props?.projectType, props?.projectId, props?.publicationId]);
  const { trackEvent } = useAnalytics<
    MediaAnalyticsEvents,
    MediaEventsWithProperties
  >(analyticsParams);
  const trackingCleanupRef = useRef<(() => void) | null>(null);

  // Memoize the debounced function to prevent recreation
  const debouncedTrackEvent = useMemo(
    () => debounce(trackEvent, 1500),
    [trackEvent],
  );

  // Core state
  const state = useRef<VideoAnalyticsState>({
    totalWatchTime: 0,
    actualWatchTime: 0,
    skippedRanges: [],
    currentSession: null,
    isPlaying: false,
    hasStarted: false,
    lastKnownPosition: 0,
    seekStartTime: 0,
  });

  // Refs for tracking
  const adapterRef = useRef<VideoAdapter | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const seekStartRef = useRef<number | null>(null);

  // Utility functions
  const getCurrentTime = useCallback(() => {
    return adapterRef.current?.getCurrentTime() ?? 0;
  }, []);

  const getDuration = useCallback(() => {
    return round(adapterRef.current?.getDuration() ?? 0);
  }, []);

  // Add skip range with overlap handling
  const addSkipRange = useCallback((start: number, end: number) => {
    if (start >= end) {
      return;
    }

    const newRange: SkipRange = { start, end };
    const ranges = state.current.skippedRanges;

    // Find overlapping ranges and merge them
    const overlappingIndexes: number[] = [];

    for (const [index, range] of ranges.entries()) {
      if (start <= range.end && end >= range.start) {
        overlappingIndexes.push(index);
      }
    }

    if (overlappingIndexes.length > 0) {
      // Merge with existing ranges
      const mergedRange = { start, end };
      for (const index of overlappingIndexes) {
        const existing = ranges[index];
        mergedRange.start = Math.min(mergedRange.start, existing.start);
        mergedRange.end = Math.max(mergedRange.end, existing.end);
      }

      // Remove old ranges and add merged one
      for (const index of [...overlappingIndexes].reverse()) {
        ranges.splice(index, 1);
      }
      ranges.push(mergedRange);
    } else {
      // Add new range
      ranges.push(newRange);
    }

    // Trigger re-render to update analytics data
    setSkipRangesTrigger((prev) => prev + 1);
  }, []);

  const getTotalSkippedTime = useCallback(() => {
    const total = state.current.skippedRanges.reduce((total, range) => {
      const rangeDuration = range.end - range.start;
      return total + rangeDuration;
    }, 0);
    return total;
  }, []);

  // Session management
  const startWatchSession = useCallback(() => {
    const currentTime = getCurrentTime();
    state.current.currentSession = {
      startTime: Date.now() / 1000,
      duration: 0,
    };
    state.current.lastKnownPosition = currentTime;
    setSessionTrigger((prev) => prev + 1);
  }, [getCurrentTime]);

  const endWatchSession = useCallback(() => {
    if (!state.current.currentSession) return;

    const now = Date.now() / 1000;
    const session = state.current.currentSession;
    session.endTime = now;
    session.duration = now - session.startTime;

    // Add to total watch time
    state.current.totalWatchTime += session.duration;

    // Update actual watch time
    const skippedTime = getTotalSkippedTime();
    state.current.actualWatchTime = Math.max(
      0,
      state.current.totalWatchTime - skippedTime,
    );

    state.current.currentSession = null;
    setSessionTrigger((prev) => prev + 1);
  }, [getTotalSkippedTime]);

  // Tracking properties
  const getTrackingProperties = useCallback((): MediaPlayerTimedEvent => {
    const currentTime = getCurrentTime();
    const duration = getDuration();

    let currentTotalWatchTime = state.current.totalWatchTime;

    if (state.current.currentSession && state.current.isPlaying) {
      const now = Date.now() / 1000;
      const currentSessionDuration =
        now - state.current.currentSession.startTime;
      currentTotalWatchTime += currentSessionDuration;
    }

    const skippedTime = getTotalSkippedTime();
    const actualWatchTime = Math.max(0, currentTotalWatchTime - skippedTime);

    return {
      time: round(currentTime),
      current_time: round(currentTime),
      watch_time: round(currentTotalWatchTime),
      skipped_time: round(skippedTime),
      duration: round(duration),
      completion_rate:
        duration > 0 ? round((actualWatchTime / duration) * 100) : 0,
      playback_rate: adapterRef.current?.getPlaybackRate() ?? 1,
    };
  }, [getCurrentTime, getDuration, getTotalSkippedTime]);

  // Event handlers
  const handlePlay = useCallback(() => {
    state.current.isPlaying = true;
    startWatchSession();

    if (state.current.hasStarted) {
      trackEvent(
        MediaAnalyticsEvents.MediaPlayerResumedEvent,
        getTrackingProperties(),
      );
    } else {
      trackEvent(MediaAnalyticsEvents.MediaPlayerPlayEvent, {
        ...getTrackingProperties(),
      });
      state.current.hasStarted = true;
    }
    // Start heartbeat
    startHeartbeat();
  }, [trackEvent, getTrackingProperties, startWatchSession]);

  const startHeartbeat = useCallback(() => {
    if (!heartbeatIntervalRef.current) {
      heartbeatIntervalRef.current = setInterval(() => {
        trackEvent(
          MediaAnalyticsEvents.MediaPlayerHeartbeatEvent,
          getTrackingProperties(),
        );
      }, HEARTBEAT_INTERVAL);
    }
  }, [trackEvent, getTrackingProperties]);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);
  const handlePause = useCallback(() => {
    state.current.isPlaying = false;
    endWatchSession();

    trackEvent(
      MediaAnalyticsEvents.MediaPlayerPausedEvent,
      getTrackingProperties(),
    );

    // Stop heartbeat
    stopHeartbeat();
  }, [trackEvent, getTrackingProperties, endWatchSession]);

  const handleSeeking = useCallback(() => {
    // End current session when seeking to prevent inflated watch time
    endWatchSession();
  }, [getCurrentTime, endWatchSession]);

  const handleSeeked = useCallback(() => {
    const seekEndTime = getCurrentTime();
    const seekStartTime = state.current.seekStartTime;
    const seekDifference = seekEndTime - seekStartTime;

    // Only track forward seeks as skips with a minimum threshold
    if (seekEndTime > seekStartTime && seekDifference >= 1) {
      addSkipRange(seekStartTime, seekEndTime);
    }

    debouncedTrackEvent(MediaAnalyticsEvents.MediaPlayerSeekEvent, {
      ...getTrackingProperties(),
      seek_start: round(seekStartTime),
      seek_end: round(seekEndTime),
    });

    seekStartRef.current = null;
    state.current.lastKnownPosition = seekEndTime;

    // Start new session after seeking
    startWatchSession();
  }, [
    getCurrentTime,
    addSkipRange,
    debouncedTrackEvent,
    getTrackingProperties,
    startWatchSession,
  ]);

  const handleEnded = useCallback(() => {
    state.current.isPlaying = false;
    endWatchSession();

    // Stop heartbeat
    stopHeartbeat();

    // Track completion event
    trackEvent(
      MediaAnalyticsEvents.MediaPlayerCompletedEvent,
      getTrackingProperties(),
    );
  }, [endWatchSession, trackEvent, getTrackingProperties, stopHeartbeat]);

  const handleDispose = useCallback(() => {
    endWatchSession();
    trackEvent(
      MediaAnalyticsEvents.MediaPlayerHeartbeatEvent,
      getTrackingProperties(),
    );

    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, [endWatchSession, trackEvent, getTrackingProperties]);

  // Setup player tracking
  const setupPlayerTracking = useCallback(
    (playerOrVideo: Player | HTMLVideoElement) => {
      // Clean up previous tracking first!
      // in case it's called multiple times if media changes in parent
      if (trackingCleanupRef.current) {
        trackingCleanupRef.current();
        trackingCleanupRef.current = null;
      }

      // Create appropriate adapter
      if ('on' in playerOrVideo && typeof playerOrVideo.on === 'function') {
        // It's a Video.js Player
        adapterRef.current = new VideoJsAdapter(playerOrVideo as Player);
      } else {
        // It's a native HTMLVideoElement
        adapterRef.current = new NativeVideoAdapter(
          playerOrVideo as HTMLVideoElement,
        );
      }

      const adapter = adapterRef.current;

      // Track impression
      trackEvent(MediaAnalyticsEvents.MediaPlayerImpressionEvent);

      // Core video events - now using adapter.on()
      adapter.on('play', handlePlay);
      adapter.on('pause', handlePause);
      adapter.on('seeking', handleSeeking);
      adapter.on('seeked', handleSeeked);
      adapter.on('ended', handleEnded);

      // Time tracking
      adapter.on('timeupdate', () => {
        const currentTime = getCurrentTime();
        state.current.lastKnownPosition = currentTime;
      });

      // Handle seek initiation for native video
      if (playerOrVideo instanceof HTMLVideoElement) {
        const handleSeekInitiation = () => {
          const preSeekTime = adapter.getCurrentTime();
          state.current.seekStartTime = preSeekTime ?? 0;
        };

        playerOrVideo.addEventListener('mousedown', handleSeekInitiation);
        playerOrVideo.addEventListener('touchstart', handleSeekInitiation);

        trackingCleanupRef.current = () => {
          adapter.off('play', handlePlay);
          adapter.off('pause', handlePause);
          adapter.off('seeking', handleSeeking);
          adapter.off('seeked', handleSeeked);
          adapter.off('ended', handleEnded);
          playerOrVideo.removeEventListener('mousedown', handleSeekInitiation);
          playerOrVideo.removeEventListener('touchstart', handleSeekInitiation);
        };
      } else {
        // Video.js specific - progress control seek initiation
        const player = playerOrVideo as Player;
        const progressControl = player
          .getChild('controlBar')
          ?.getChild('ProgressControl')
          ?.el();

        const handleSeekInitiation = () => {
          const preSeekTime = adapter.getCurrentTime();
          state.current.seekStartTime = preSeekTime ?? 0;
        };

        progressControl?.addEventListener('pointerdown', handleSeekInitiation);

        // TODO refactor with a use effect and a player prop in the hook
        // but not mandatory for now
        trackingCleanupRef.current = () => {
          adapter.off('play', handlePlay);
          adapter.off('pause', handlePause);
          adapter.off('seeking', handleSeeking);
          adapter.off('seeked', handleSeeked);
          adapter.off('ended', handleEnded);
          progressControl?.removeEventListener(
            'pointerdown',
            handleSeekInitiation,
          );
        };
      }
    },
    [
      handlePlay,
      handlePause,
      handleSeeking,
      handleSeeked,
      handleEnded,
      handleDispose,
      getCurrentTime,
      trackEvent,
    ],
  );

  useEffect(() => {
    // cleanup event handlers on unmount
    return () => {
      trackingCleanupRef.current?.();
    };
  }, []);

  // Handle page unload
  const handleUnload = useCallback(() => {
    endWatchSession();

    // Send final heartbeat if still playing
    if (state.current.isPlaying) {
      trackEvent(
        MediaAnalyticsEvents.MediaPlayerHeartbeatEvent,
        getTrackingProperties(),
      );
    }
  }, [endWatchSession, trackEvent, getTrackingProperties]);

  // Reset tracking state
  const resetTracking = useCallback(() => {
    state.current = {
      totalWatchTime: 0,
      actualWatchTime: 0,
      skippedRanges: [],
      currentSession: null,
      isPlaying: false,
      hasStarted: false,
      lastKnownPosition: 0,
      seekStartTime: 0,
    };

    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);

  // Update analytics data more frequently when playing
  const [updateTrigger, setUpdateTrigger] = useState(0);
  const [skipRangesTrigger, setSkipRangesTrigger] = useState(0);
  const [sessionTrigger, setSessionTrigger] = useState(0);

  useEffect(() => {
    if (state.current.isPlaying) {
      const interval = setInterval(() => {
        setUpdateTrigger((prev: number) => prev + 1);
      }, 1000); // Update every second when playing

      return () => clearInterval(interval);
    }
    return undefined;
  }, [state.current.isPlaying]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, []);

  // Computed values
  const analyticsData = useMemo(() => {
    // Calculate current actual watch time dynamically
    let currentTotalWatchTime = state.current.totalWatchTime;

    // Add current session time if playing
    if (state.current.currentSession && state.current.isPlaying) {
      const now = Date.now() / 1000;
      const currentSessionDuration =
        now - state.current.currentSession.startTime;
      currentTotalWatchTime += currentSessionDuration;
    }

    const skippedTime = getTotalSkippedTime();
    const actualWatchTime = Math.max(0, currentTotalWatchTime - skippedTime);

    return {
      totalWatchTime: currentTotalWatchTime,
      actualWatchTime: actualWatchTime,
      skippedTime: skippedTime,
      skippedRanges: state.current.skippedRanges,
      isPlaying: state.current.isPlaying,
      hasStarted: state.current.hasStarted,
      currentPosition: getCurrentTime(),
      duration: getDuration(),
      completionRate: getTrackingProperties().completion_rate,
    };
  }, [
    getTotalSkippedTime,
    getCurrentTime,
    getDuration,
    getTrackingProperties,
    updateTrigger,
    skipRangesTrigger,
    sessionTrigger,
    // Add state.current.skippedRanges.length to force re-computation when ranges change
    state.current.skippedRanges.length,
  ]);

  return {
    setupPlayerTracking,
    handleUnload,
    resetTracking,
    getTrackingProperties,
    analyticsData,
  };
};
